import React, { useState, useEffect } from 'react';
import DialogModal from './DialogModal';

/**
 * Custom "Upgrade Required" dialog for plan-tier gating — replaces the
 * transient toast.info(...) previously used by Layout.js's nav-lock click
 * interception, ProtectedRoute.js's direct-URL redirect,
 * FinancePage.js's tab-lock click interception, and LockedFeature.js's
 * per-button lock. A DialogModal (this app's own replacement for browser
 * alert/confirm/prompt) reads as more deliberate than a toast for
 * something the user needs to actually notice and dismiss, and gives room
 * to show which specific feature is locked.
 *
 * Usage: call showUpgradeRequired(message) from anywhere — no local state
 * or prop drilling needed, same drop-in call shape as toast.info(message)
 * had. Mount <UpgradeRequiredDialogHost /> once near the app root (see
 * App.js, next to <Toaster />) to actually render it.
 */
let listeners = [];

export function showUpgradeRequired(message) {
  const text = message || 'This feature requires a higher subscription plan. Contact your school administrator to upgrade.';
  listeners.forEach((listener) => listener(text));
}

export function UpgradeRequiredDialogHost() {
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const listener = (text) => setMessage(text);
    listeners.push(listener);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  }, []);

  return (
    <DialogModal
      isOpen={!!message}
      onClose={() => setMessage(null)}
      title="Upgrade Required"
      message={message || ''}
      type="warning"
      primaryAction={{ label: 'OK' }}
    />
  );
}
